/**
 * @module app.cm
 */

import { autoinject, observable } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { SystemReviewService } from 'app/cm/services/system-review-service';
import { IQuestionComponent, IQuestionSavedResponse, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ApiDate } from 'app/shared/models/essette-api';
import { apiCall } from 'app/shared/lib/essette-api';
import { MemberService } from 'app/cd/services/member-service';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { getYear, parseISO } from 'date-fns';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import * as CM from 'app/cm/models/essette-api';

@autoinject
export class QuestionSystemReviewCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties ************************************////

    protected caseId: string | null = null;

    @observable({ changeHandler: 'validate' })
    protected editing: number = -1;
    protected valid: boolean = true;

    protected get memberId(): string | null {
        const caseAttachment = this.context.attachment as CM.CaseAssessmentAttachment;
        return caseAttachment.member && caseAttachment.member.id || null;
    }

    protected systemReviewIllnesses: CM.SystemReviewIllness[] = [];
    protected systems: CM.System[] = [];        // The Systems (including SystemIllnesses) bound to selects
    protected years: number[] = [];             // Years that are valid for the member based on DOB, bound to Onset select
    private readonly currentYear = getYear(Date.now());


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly context: AssessmentContext,
        protected readonly service: SystemReviewService,
        protected readonly memberService: MemberService,
        protected readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        if(this.context.attachment.attachedTo?.type?.code !== 'CM.Case') {
            throw new Error('Invalid question type loaded for attachment. Expected attachment target type of CM.Case')
        }
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.caseId = this.context.attachment.attachedTo?.id || null;

        this.loadData();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected validate() {
        const editing = this.systemReviewIllnesses?.[this.editing];
        this.valid = !editing || !isNullOrUndefined(editing.system?.illness && editing.onset?.year);
    }

    protected async loadData(): Promise<void> {
        if(!(this.question && this.caseId)) {
            this.logger.error('loadData() is missing information');
        }

        const lastSessionId = this.context.attachment.lastSession?.id || null;

        const [systems, systemReviewIllnesses, memberDob] = await Promise.all([
            this.loadSystemsAndIllnesses(),
            this.loadSystemReviewIllnesses(lastSessionId),
            this.loadMemberBirthDate()
        ]);

        this.systems = systems as CM.System[];
        this.systemReviewIllnesses = systemReviewIllnesses as CM.SystemReviewIllness[];

        const birthYear = memberDob && getYear(parseISO(String(memberDob))) || null;

        if(birthYear !== null) {
            // Add years to drop down going back to member's birth year
            for(let i = this.currentYear; i >= birthYear; i--) {
                this.years.push(i);
            }
        }

        this.context.unsavedChanges = ((this.systemReviewIllnesses?.length ?? 0) > 0); //Don't allow save or complete if there are no illnesses
    }

    // INFO [Matt] System dropdown has a one-way binding, so this function
    // responds to the change event and sets a copy of the selected system.
    protected updateValue(illness: CM.SystemIllness, system: CM.System) {
        illness.system = system ? Object.assign({}, system) : null;

        this.context.unsavedChanges = true;
    }

    protected removeIllness(illness: CM.SystemReviewIllness): void {
        if(this.systemReviewIllnesses) {
            const idx = this.systemReviewIllnesses.indexOf(illness);
            if(idx > -1) {
                this.systemReviewIllnesses.splice(idx, 1);
                if(idx === this.editing) {
                    this.editing = -1;
                } else if(idx < this.editing) {
                    this.editing -= 1;
                }
            }

            this.context.unsavedChanges = ((this.systemReviewIllnesses?.length ?? 0) > 0); //Don't allow save or complete if there are no illnesses
        }
    }

    protected addIllness() {
        this.systemReviewIllnesses = this.systemReviewIllnesses || [];
        this.systemReviewIllnesses.push({ onset: { year: this.years[0] || this.currentYear } });
        this.editing = this.systemReviewIllnesses.length - 1;

        this.context.unsavedChanges = true;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadSystemsAndIllnesses(): Promise<CM.System[]> {
        try {
            this.state.beginLoad();
            const includes: string[] = ['illnesses'];
            const fields: string[] = [
                'description',
                'illnesses.description',
                'illnesses.id'
            ];
            const response = await this.service.loadSystems({}, includes, fields);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadSystemReviewIllnesses(sessionId: string | null): Promise<CM.SystemReviewIllness[] | null> {
        if(!sessionId) { return null; }

        if(!(this.question && this.question.id)) {
            throw new Error('Missing required data to load system review')
        }

        try {
            this.state.beginLoad();

            const id = String(sessionId);
            const includes: string[] = ['system', 'system.illness'];
            const fields: string[] = [
                'onset.description',
                'onset.year',
                'system.description',
                'system.id',
                'system.illness',
                'system.illness.description',
                'system.illness.id'
            ];
            const questionId = String(this.question.id);

            const response = await this.service.loadSystemReviewQuestionIllnesses(id, questionId, { all: true }, includes, fields);

            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveSystemReview(systemReview: CM.SystemReview): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.insertSystemReview(systemReview);
            return response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async loadMemberBirthDate(): Promise<ApiDate | null> {
        if(!this.memberId) {
            return null;
        }

        const fields: string[] = ['birthDate'];
        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMember(this.memberId, [], fields);
            if(response?.results.length > 0) {
                return response.results[0].birthDate || null;
            }
            return null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }


    ////****************** IQuestionComponent ****************************************************////
    public async saveAnswer(session?: CM.CaseAssessmentSession): Promise<IQuestionSavedResponse> {
        if(!session) {
            throw new Error("Saving System Review requires a current Session ID");
        }

        const toApiSystemReviewIllness = (illness: CM.SystemReviewIllness) => {
            return {
                system: { illness: { id: illness.system?.illness?.id || null } },
                onset: { year: illness.onset?.year || null }
            } as CM.SystemReviewIllness;
        }

        const response: IQuestionSavedResponse = {
            question: this.question,
            isSuccessful: true
        };

        const newSystemReview: CM.SystemReview = {
            session: { id: session.id },
            question: { id: this.question.id || null },
            illnesses: (this.systemReviewIllnesses || []).map(toApiSystemReviewIllness)
        };

        response.isSuccessful = await this.saveSystemReview(newSystemReview);
        return response;
    }

    public getAnswers(): CM.CaseAssessmentAnswer[] {
        return [];
    }

}
